import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircle, 
  Circle, 
  ArrowRight,
  Users,
  Building2,
  MessageSquare,
  Settings,
  FileText,
  CreditCard,
  Bell,
  Shield
} from "lucide-react";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  actions: string[];
}

const providerSteps: OnboardingStep[] = [
  {
    id: "profile",
    title: "Complete Your Profile",
    description: "Add your organization details, NPI, and contact information",
    icon: <Building2 className="w-5 h-5" />,
    completed: false,
    actions: [
      "Upload facility license",
      "Verify NPI number",
      "Add facility photos",
      "Complete organization details"
    ]
  },
  {
    id: "subscription",
    title: "Choose Your Plan",
    description: "Select the subscription tier that fits your needs",
    icon: <CreditCard className="w-5 h-5" />,
    completed: false,
    actions: [
      "Review pricing tiers",
      "Select appropriate plan",
      "Set up payment method",
      "Activate subscription"
    ]
  },
  {
    id: "listings",
    title: "Create Your First Listing",
    description: "Post available beds and care level information",
    icon: <FileText className="w-5 h-5" />,
    completed: false,
    actions: [
      "Add facility details",
      "Specify care levels",
      "Set waiver types",
      "Upload facility photos"
    ]
  },
  {
    id: "notifications",
    title: "Set Up Notifications",
    description: "Configure how you receive referral alerts",
    icon: <Bell className="w-5 h-5" />,
    completed: false,
    actions: [
      "Enable email notifications",
      "Set notification preferences",
      "Add backup contacts",
      "Test notification delivery"
    ]
  }
];

const placementSteps: OnboardingStep[] = [
  {
    id: "profile",
    title: "Complete Your Profile",
    description: "Add your role, organization, and contact information",
    icon: <Users className="w-5 h-5" />,
    completed: false,
    actions: [
      "Verify your professional credentials",
      "Add organization details",
      "Set up your profile",
      "Specify your role and counties served"
    ]
  },
  {
    id: "search",
    title: "Learn to Search",
    description: "Master the search and filtering tools",
    icon: <Settings className="w-5 h-5" />,
    completed: false,
    actions: [
      "Try different filter combinations",
      "Save your common searches",
      "Understand listing details",
      "Practice using advanced filters"
    ]
  },
  {
    id: "referral",
    title: "Submit Your First Referral",
    description: "Practice the referral submission process",
    icon: <MessageSquare className="w-5 h-5" />,
    completed: false,
    actions: [
      "Complete HIPAA acknowledgment",
      "Fill out referral form",
      "Attach necessary documents",
      "Submit and track status"
    ]
  },
  {
    id: "compliance",
    title: "Review Compliance",
    description: "Understand HIPAA requirements and best practices",
    icon: <Shield className="w-5 h-5" />,
    completed: false,
    actions: [
      "Review HIPAA guidelines",
      "Understand data protection",
      "Learn about secure sharing",
      "Complete compliance training"
    ]
  }
];

const OnboardingGuide = () => {
  const [activeTab, setActiveTab] = useState("provider");
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const currentSteps = activeTab === "provider" ? providerSteps : placementSteps;
  const progress = (completedSteps.size / currentSteps.length) * 100;

  const toggleStep = (stepId: string) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
    }
    setCompletedSteps(newCompleted);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Welcome to CareKonnect!</h1>
        <p className="text-lg text-muted-foreground">
          Let's get you set up and ready to connect with care partners in Minnesota
        </p>
        <Progress value={progress} className="w-full max-w-md mx-auto" />
        <p className="text-sm text-muted-foreground">
          {completedSteps.size} of {currentSteps.length} steps completed
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="provider">Provider Setup</TabsTrigger>
          <TabsTrigger value="placement">Placement Professional Setup</TabsTrigger>
        </TabsList>

        <TabsContent value="provider" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Provider Onboarding
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Complete these steps to start receiving referrals and connecting with placement professionals.
              </p>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {providerSteps.map((step, index) => (
              <Card key={step.id} className={`transition-all ${completedSteps.has(step.id) ? 'border-green-200 bg-green-50/50' : ''}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-auto"
                        onClick={() => toggleStep(step.id)}
                      >
                        {completedSteps.has(step.id) ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <Circle className="w-6 h-6 text-muted-foreground" />
                        )}
                      </Button>
                      <div className="flex items-center gap-3">
                        {step.icon}
                        <div>
                          <CardTitle className="text-lg">{step.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline">Step {index + 1}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-3">
                    {step.actions.map((action, actionIndex) => (
                      <div key={actionIndex} className="flex items-center gap-2 text-sm">
                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        <span>{action}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="placement" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Placement Professional Onboarding
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Get ready to find the perfect care placements for your clients across Minnesota.
              </p>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {placementSteps.map((step, index) => (
              <Card key={step.id} className={`transition-all ${completedSteps.has(step.id) ? 'border-green-200 bg-green-50/50' : ''}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-auto"
                        onClick={() => toggleStep(step.id)}
                      >
                        {completedSteps.has(step.id) ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <Circle className="w-6 h-6 text-muted-foreground" />
                        )}
                      </Button>
                      <div className="flex items-center gap-3">
                        {step.icon}
                        <div>
                          <CardTitle className="text-lg">{step.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline">Step {index + 1}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-3">
                    {step.actions.map((action, actionIndex) => (
                      <div key={actionIndex} className="flex items-center gap-2 text-sm">
                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        <span>{action}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Card className="bg-gradient-to-r from-primary/5 to-accent/5">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Need Help Getting Started?</h3>
              <p className="text-muted-foreground">
                Our team is here to help you through the onboarding process
              </p>
            </div>
            <Button>Contact Support</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingGuide;